import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react'
import {
  ACTIVE_WORKSPACE_STORAGE_KEY,
  ACTIVE_WORKSPACE_TAB_STORAGE_KEY,
} from '../context/workspaceContextCore'

const PERSIST_PREFIX = 'tl:persist:'
const CLEAR_EVENT = 'tl:persist:clear'
const SESSION_USER_STORAGE_KEY = 'timelining-session-user'

const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect

type PersistedStateStorage = 'session' | 'local'
type PersistedStateScope = 'user-workspace' | 'none'

type PersistedStateOptions = {
  schemaVersion?: number
  storage?: PersistedStateStorage
  scope?: PersistedStateScope
  writeDelayMs?: number
  skipInitialWrite?: boolean
}

const safeRun = <T,>(fn: () => T, fallback: T): T => {
  try {
    return fn()
  } catch {
    return fallback
  }
}

const readScope = (): { uid: string | null; workspaceId: string | null } => {
  if (typeof window === 'undefined') return { uid: null, workspaceId: null }
  return safeRun(() => {
    const raw = window.sessionStorage.getItem(SESSION_USER_STORAGE_KEY)
    const parsed = raw ? (JSON.parse(raw) as { user?: { uid?: string } } | null) : null
    const uid = parsed?.user?.uid ?? null
    const tabWorkspaceId = window.sessionStorage.getItem(ACTIVE_WORKSPACE_TAB_STORAGE_KEY)
    const deviceWorkspaceId = window.localStorage.getItem(ACTIVE_WORKSPACE_STORAGE_KEY)
    const workspaceId = tabWorkspaceId || deviceWorkspaceId || null
    return { uid, workspaceId }
  }, { uid: null, workspaceId: null })
}

const getStorage = (storage: PersistedStateStorage): Storage | null => {
  if (typeof window === 'undefined') return null
  return storage === 'local' ? window.localStorage : window.sessionStorage
}

const buildStorageKey = (
  key: string,
  schemaVersion: number,
  uid: string | null,
  workspaceId: string | null,
  scope: PersistedStateScope,
): string | null =>
  scope === 'none'
    ? key
    : uid && workspaceId
    ? `${PERSIST_PREFIX}v${schemaVersion}:${uid}:${workspaceId}:${key}`
    : null

const resolveDefault = <T,>(defaultValue: T | (() => T)): T =>
  typeof defaultValue === 'function' ? (defaultValue as () => T)() : defaultValue

const readStoredValue = <T,>(
  storageKey: string | null,
  storage: PersistedStateStorage,
  fallback: T,
): T => {
  if (!storageKey || typeof window === 'undefined') return fallback
  const storageTarget = getStorage(storage)
  return safeRun(() => {
    const raw = storageTarget?.getItem(storageKey)
    if (raw == null) return fallback
    return JSON.parse(raw) as T
  }, fallback)
}

export function usePersistedState<T>(
  key: string,
  defaultValue: T | (() => T),
  options?: PersistedStateOptions,
): [T, Dispatch<SetStateAction<T>>] {
  const schemaVersion = options?.schemaVersion ?? 1
  const storage = options?.storage ?? 'session'
  const scope = options?.scope ?? 'user-workspace'
  const writeDelayMs = Math.max(0, options?.writeDelayMs ?? 0)
  const skipInitialWrite = options?.skipInitialWrite ?? false

  const defaultRef = useRef(defaultValue)
  useEffect(() => {
    defaultRef.current = defaultValue
  })

  const { uid, workspaceId } = readScope()
  const storageKey = buildStorageKey(key, schemaVersion, uid, workspaceId, scope)
  const keyIdentifier = `${storage}:${scope}:${schemaVersion}:${key}:::${storageKey}`

  const [state, setState] = useState<T>(() =>
    readStoredValue(storageKey, storage, resolveDefault(defaultValue)),
  )
  const [prevKeyIdentifier, setPrevKeyIdentifier] = useState(keyIdentifier)

  const pendingWriteRef = useRef<{
    storageKey: string
    storageTarget: Storage
    serialized: string
  } | null>(null)
  const debounceTimerRef = useRef<number | null>(null)
  const hasPersistEffectRunRef = useRef(false)
  const lastEffectStorageKeyRef = useRef<string | null>(storageKey)

  const flushPending = useCallback(() => {
    if (debounceTimerRef.current !== null) {
      window.clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = null
    }
    const pending = pendingWriteRef.current
    if (pending) {
      pendingWriteRef.current = null
      safeRun(() => {
        pending.storageTarget.setItem(pending.storageKey, pending.serialized)
      }, undefined)
    }
  }, [])

  if (prevKeyIdentifier !== keyIdentifier) {
    setPrevKeyIdentifier(keyIdentifier)
    const nextVal = readStoredValue(storageKey, storage, resolveDefault(defaultValue))
    setState(nextVal)
  }

  useIsomorphicLayoutEffect(() => {
    return () => {
      flushPending()
    }
  }, [flushPending, keyIdentifier])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const { uid: currentUid, workspaceId: currentWsId } = readScope()
    const currentStorageKey = buildStorageKey(key, schemaVersion, currentUid, currentWsId, scope)
    const storageTarget = getStorage(storage)

    const isKeyChange = lastEffectStorageKeyRef.current !== currentStorageKey
    lastEffectStorageKeyRef.current = currentStorageKey

    if (isKeyChange) {
      if (skipInitialWrite) return
    } else if (!hasPersistEffectRunRef.current) {
      hasPersistEffectRunRef.current = true
      if (skipInitialWrite) return
    }

    if (!currentStorageKey || !storageTarget) return

    const serialized = JSON.stringify(state)

    const write = () => {
      safeRun(() => {
        storageTarget.setItem(currentStorageKey, serialized)
      }, undefined)
      pendingWriteRef.current = null
      debounceTimerRef.current = null
    }

    if (writeDelayMs === 0) {
      if (debounceTimerRef.current !== null) {
        window.clearTimeout(debounceTimerRef.current)
        debounceTimerRef.current = null
      }
      pendingWriteRef.current = null
      write()
      return
    }

    if (debounceTimerRef.current !== null) {
      window.clearTimeout(debounceTimerRef.current)
    }
    pendingWriteRef.current = {
      storageKey: currentStorageKey,
      storageTarget,
      serialized,
    }
    debounceTimerRef.current = window.setTimeout(write, writeDelayMs)
  }, [flushPending, key, schemaVersion, scope, skipInitialWrite, state, storage, writeDelayMs])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const handler = () => setState(resolveDefault(defaultRef.current))
    window.addEventListener(CLEAR_EVENT, handler)
    return () => window.removeEventListener(CLEAR_EVENT, handler)
  }, [])

  return [state, setState]
}

export function clearAllPersistedState(): void {
  if (typeof window === 'undefined') return
  safeRun(() => {
    const keysToRemove: string[] = []
    for (let i = 0; i < window.sessionStorage.length; i += 1) {
      const k = window.sessionStorage.key(i)
      if (k && k.startsWith(PERSIST_PREFIX)) keysToRemove.push(k)
    }
    keysToRemove.forEach((k) => window.sessionStorage.removeItem(k))
  }, undefined)
  safeRun(() => {
    window.dispatchEvent(new CustomEvent(CLEAR_EVENT))
  }, undefined)
}
